import React, { useEffect, useRef, useState } from 'react';
import styles from './PanelAgregarResponsive.module.css';

/**
 * Componente genérico para mostrar un panel de "Agregar [item]"
 *
 * Desktop: Renderiza el componente en la columna lateral izquierda
 * Mobile: Renderiza un botón "Agregar" y un panel expandible debajo de la lista
 *
 * Props:
 * - Componente: Componente a renderizar (NuevoClubFiltro, NuevaPersonaFiltro)
 * - objeto: Tipo de item ('club', 'persona')
 * - lista: Lista de resultados (para saber si mostrar)
 * - onListaCargada: Callback después de crear
 * - puedeEditar: Boolean de permisos
 * - buscando: Boolean para saber si está buscando
 * - children: El contenido central (tabla, lista, etc.)
 */
const PanelAgregarResponsive = ({
  Componente,
  objeto,
  lista,
  onListaCargada,
  puedeEditar,
  buscando,
  children,
}) => {
  const [isMobile, setIsMobile] = useState(false);
  const [mostrarPanelMobile, setMostrarPanelMobile] = useState(false);
  const panelMobileRef = useRef(null);

  // Inicializar isMobile cuando el componente se monta
  useEffect(() => {
    setIsMobile(window.innerWidth <= 1024);
  }, []);

  // Detectar cambios de tamaño de ventana
  useEffect(() => {
    const handleResize = () => {
      const newIsMobile = window.innerWidth <= 1024;
      setIsMobile(newIsMobile);
      if (!newIsMobile) {
        setMostrarPanelMobile(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Auto-scroll al panel cuando se abre en mobile
  useEffect(() => {
    if (mostrarPanelMobile && panelMobileRef.current) {
      setTimeout(() => {
        panelMobileRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    }
  }, [mostrarPanelMobile]);

  const togglePanelMobile = () => {
    setMostrarPanelMobile(!mostrarPanelMobile);
  };

  const objetoCapitalizado = objeto.charAt(0).toUpperCase() + objeto.slice(1);

  return (
    <div className={styles.baseLista}>
      {/* Columna lateral izquierda: visible solo en desktop */}
      <div className={`${styles.columnaLateral} ${!isMobile ? styles.visible : ''}`}>
        {!isMobile && !buscando && puedeEditar && lista && (
          <Componente onMostrarLista={onListaCargada} />
        )}
      </div>

      {/* Columna principal con contenido y controles mobile */}
      <div className={styles.listaContenido}>
        {children}

        {/* Botón para agregar en móvil (debajo de la tabla/lista) */}
        {/* Solo mostrar cuando: mobile AND tiene permisos AND lista existe (se hizo búsqueda) AND no está buscando */}
        {isMobile && puedeEditar && lista !== null && !buscando && (
          <div className={styles.botonNuevoItemMobile}>
            <button
              className={styles.botonAgregarItem}
              onClick={togglePanelMobile}
              title={`Agregar nuevo ${objeto}`}
            >
              ➕ Agregar {objetoCapitalizado}
            </button>
          </div>
        )}

        {/* Panel para agregar en móvil (debajo del botón) */}
        {/* Solo mostrar cuando: mobile AND tiene permisos AND lista existe AND panel está abierto */}
        {isMobile && puedeEditar && lista !== null && mostrarPanelMobile && (
          <div className={styles.panelNuevoItemMobile} ref={panelMobileRef}>
            <div className={styles.encabezadoPanelNuevoItem}>
              <h3>Agregar nuevo {objeto}</h3>
              <button
                className={styles.botonCerrarPanel}
                onClick={togglePanelMobile}
                title="Cerrar"
              >
                ✕
              </button>
            </div>
            <Componente onMostrarLista={onListaCargada} autoAbrir={true} />
          </div>
        )}
      </div>

      {/* Columna lateral derecha: vacía en desktop */}
      <div className={`${styles.columnaLateral}`} />
    </div>
  );
};

export default PanelAgregarResponsive;
